import { useCallback, useEffect, useState } from 'react';

import { Banner } from '@/components/ui/Banner';
import { FEATURES, useFeaturePayload } from '@/features/feature-flags';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

interface DynamicBannerConfig {
  banner_id: string;
  header_text: string;
  banner_level?: 'default' | 'warning' | 'danger';
  description_text?: string | null;
}

const DYNAMIC_BANNER_DISMISSED_LOCAL_STORAGE_KEY = 'dynamic-banner-dismissed';

function useDismissedBannerIds() {
  const [dismissedBannerIds, setDismissedBannerIds] = useState<string[]>(
    getLocalStorageItem(DYNAMIC_BANNER_DISMISSED_LOCAL_STORAGE_KEY) ?? []
  );

  useEffect(() => {
    setLocalStorageItem(
      DYNAMIC_BANNER_DISMISSED_LOCAL_STORAGE_KEY,
      dismissedBannerIds
    );
  }, [dismissedBannerIds]);

  const addDismissedBannerId = useCallback((bannerId: string) => {
    setDismissedBannerIds((prev) => {
      if (prev.includes(bannerId)) return prev;
      return [...prev, bannerId];
    });
  }, []);

  return {
    dismissedBannerIds,
    addDismissedBannerId,
  };
}

const DynamicBanner = ({
  config,
  onDismiss,
}: {
  config: DynamicBannerConfig;
  onDismiss: (bannerId: string) => void;
}) => {
  const variant = config.banner_level || 'default';

  const handleDismiss = () => {
    onDismiss(config.banner_id);
  };

  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      className="mx-auto w-full max-w-2xl shrink-0 px-12 pt-4 max-h-[40dvh] overflow-y-auto"
    >
      <Banner
        title={config.header_text}
        subtitle={config.description_text || ''}
        variant={variant}
        onClose={handleDismiss}
      />
    </div>
  );
};

export const DynamicBanners = () => {
  const configs = useFeaturePayload<DynamicBannerConfig[]>(
    FEATURES.DYNAMIC_BANNER
  );
  const { dismissedBannerIds, addDismissedBannerId } = useDismissedBannerIds();

  const validBanners: DynamicBannerConfig[] = configs
    ? configs.filter(
        (config) =>
          !!config?.banner_id &&
          !!config?.header_text &&
          !dismissedBannerIds.includes(config.banner_id)
      )
    : [];

  if (validBanners.length === 0) {
    return null;
  }

  return (
    <DynamicBanner config={validBanners[0]} onDismiss={addDismissedBannerId} />
  );
};
